import { forEach, has, get, isString } from 'lodash';

export enum MetaDataTag {
    Bold = 'bold',
    Italic = 'italic',
    Underline = 'underline',
    Strike = 'strike',
    BlockQuote = 'blockquote',
    List = 'list',
    CodeBlock = 'code-block',
    Link = 'link',
    Variable = 'variable',
    TemplateUrl = 'template-url',
    Mention = 'mention',
    Emoji = 'emoji',
    HashTag = 'hashtag',
}

export interface MetaData {
    text: string;
    tags: string[];
    attributes: object;
}

export const convertQuillToMetadata = (quill: any): MetaData[] => {
    const metaDataObj: MetaData[] = [];

    if (has(quill, 'ops')) {
        forEach(quill.ops, (opsItem: any, opsKey: any) => {
            const tags: any = [];
            let attributes: any = {};
            let text = '';

            if (isString(opsItem.insert)) {
                text = opsItem.insert;
            }

            if (opsItem.attributes) {
                // Rich Text
                if (has(opsItem, `attributes[${MetaDataTag.Bold}]`)) {
                    tags.push(MetaDataTag.Bold);
                }
                if (has(opsItem, `attributes[${MetaDataTag.Italic}]`)) {
                    tags.push(MetaDataTag.Italic);
                }
                if (has(opsItem, `attributes[${MetaDataTag.Underline}]`)) {
                    tags.push(MetaDataTag.Underline);
                }
                if (has(opsItem, `attributes[${MetaDataTag.Strike}]`)) {
                    tags.push(MetaDataTag.Strike);
                }
                if (has(opsItem, `attributes[${MetaDataTag.Link}]`)) {
                    tags.push(MetaDataTag.Link);
                    attributes.link = get(opsItem, `attributes[${MetaDataTag.Link}]`);
                }

                // Custom
                if (has(opsItem, `insert[${MetaDataTag.Variable}]`)) {
                    text = get(opsItem, `insert[${MetaDataTag.Variable}]`, '');
                    tags.push(MetaDataTag.Variable);
                    attributes = opsItem.attributes;
                } else if (has(opsItem, `insert[${MetaDataTag.TemplateUrl}]`)) {
                    text = get(opsItem, `insert[${MetaDataTag.TemplateUrl}]`, '');
                    tags.push(MetaDataTag.TemplateUrl);
                    attributes = opsItem.attributes;
                } else if (has(opsItem, `insert[${MetaDataTag.Mention}]`)) {
                    text = get(opsItem, `insert[${MetaDataTag.Mention}]`, '');
                    tags.push(MetaDataTag.Mention);
                    attributes = opsItem.attributes;
                } else if (has(opsItem, `insert[${MetaDataTag.Emoji}]`)) {
                    text = get(opsItem, `insert[${MetaDataTag.Emoji}]`, '');
                    tags.push(MetaDataTag.Emoji);
                    attributes = opsItem.attributes;
                } else if (has(opsItem, `insert[${MetaDataTag.HashTag}]`)) {
                    text = get(opsItem, `insert[${MetaDataTag.HashTag}]`, '');
                    tags.push(MetaDataTag.HashTag);
                    attributes = opsItem.attributes;
                }
            } else {
                let checkText = false;

                if (has(quill, `ops[${opsKey + 1}].attributes[${MetaDataTag.BlockQuote}]`)) {
                    tags.push(MetaDataTag.BlockQuote);
                    checkText = true;
                } else if (has(quill, `ops[${opsKey + 1}].attributes[${MetaDataTag.CodeBlock}]`)) {
                    tags.push(MetaDataTag.CodeBlock);
                    checkText = true;
                } else if (has(quill, `ops[${opsKey + 1}].attributes[${MetaDataTag.List}]`)) {
                    tags.push(MetaDataTag.List);
                    attributes.type = get(quill, `ops[${opsKey + 1}].attributes[${MetaDataTag.List}]`, '');
                    attributes.indent = get(quill, `ops[${opsKey + 1}].attributes.indent`, null) || 0;
                    checkText = true;
                }

                if (checkText) {
                    const parts = text.split('\n');

                    text = parts[parts.length - 1];
                    if (parts.length > 1 && parts[0]) {
                        metaDataObj.push({
                            text: parts[0],
                            tags: [],
                            attributes: {},
                        });
                    }
                }
            }

            if (text && text !== '\n') {
                metaDataObj.push({
                    text: text.replace(/\ufeff/g, '').replace(/\n$/, ''),
                    tags,
                    attributes,
                });
            }
        });
    }
    return metaDataObj;
};

export const convertMetadataToQuill = (metaData: any): any => {
    const ops: any = [];
    let prevTag: any = null;

    forEach(metaData, (metaItem, metaKey) => {
        let { text } = metaItem;
        let attributes: any = {};
        let insert: any = null;

        if (metaItem.tags && metaItem.tags.length) {
            forEach(metaItem.tags, (tagItem) => {
                // Rich Text
                if (tagItem === MetaDataTag.Bold) {
                    attributes[MetaDataTag.Bold] = true;
                }
                if (tagItem === MetaDataTag.Italic) {
                    attributes[MetaDataTag.Italic] = true;
                }
                if (tagItem === MetaDataTag.Underline) {
                    attributes[MetaDataTag.Underline] = true;
                }
                if (tagItem === MetaDataTag.Strike) {
                    attributes[MetaDataTag.Strike] = true;
                }
                if (tagItem === MetaDataTag.BlockQuote) {
                    ops.push({
                        insert: `\n${text}`,
                    });
                    attributes[MetaDataTag.BlockQuote] = true;
                    text = '\n';
                }
                if (tagItem === MetaDataTag.List) {
                    ops.push({
                        insert: `${prevTag !== MetaDataTag.List ? '\n' : ''}${text}`,
                    });
                    attributes[MetaDataTag.List] = metaItem.attributes.type;
                    if (metaItem.attributes.indent) {
                        attributes.indent = metaItem.attributes.indent;
                    }
                    text = '\n';
                }
                if (tagItem === MetaDataTag.Link) {
                    attributes[MetaDataTag.Link] = metaItem.attributes.link;
                }
                if (tagItem === MetaDataTag.CodeBlock) {
                    ops.push({
                        insert: `\n${text}`,
                    });
                    attributes[MetaDataTag.CodeBlock] = true;
                    text = '\n';
                }

                // Custom
                if (tagItem === MetaDataTag.Variable) {
                    insert = {
                        [MetaDataTag.Variable]: {
                            description: text,
                            ...metaItem.attributes,
                        },
                    };
                    attributes = metaItem.attributes;
                }
                if (tagItem === MetaDataTag.TemplateUrl) {
                    insert = {
                        [MetaDataTag.TemplateUrl]: {
                            text,
                            ...metaItem.attributes,
                        },
                    };
                    attributes = metaItem.attributes;
                }
                if (tagItem === MetaDataTag.Mention) {
                    insert = {
                        [MetaDataTag.Mention]: text,
                    };
                    attributes = { ...metaItem.attributes };
                }
                if (tagItem === MetaDataTag.Emoji) {
                    insert = {
                        [MetaDataTag.Emoji]: {
                            label: text,
                            ...metaItem.attributes,
                        },
                    };
                    attributes = metaItem.attributes;
                }
                if (tagItem === MetaDataTag.HashTag) {
                    insert = {
                        [MetaDataTag.HashTag]: {
                            label: text,
                            ...metaItem.attributes,
                        },
                    };
                    attributes = metaItem.attributes;
                }
                prevTag = tagItem;

                ops.push({
                    insert: insert || text,
                    attributes,
                });
            });
        } else {
            ops.push({
                insert: text,
                attributes,
            });
        }
    });
    ops.push({
        insert: '\n',
    });

    return {
        ops,
    };
};
